// ═════════════════════════════════════════════════════════════════════════════
// Dashboard — "Aurx" design (pixel-matched to screenshot)
//
// Layout
//   1. Header row: "Good <time-of-day>, <name>" + subtitle · [Filter] [+ Add Candidate]
//   2. Four stat cards:
//        • Colored rounded icon tile (top-left)
//        • Trend badge (top-right, green ↗ / red ↘)
//        • Huge number (48, 284, 23, 67)
//        • Subtitle (Total Clients / Total Candidates / Open Requirements / Hired This Month)
//        • "vs last month" caption
//   3. Hiring Pipeline (left, gradient horizontal bars with value chip + % on right)
//      + LIVE badge in header
//   4. Monthly Activity (right, grouped bar chart: Candidates vs Hired, last 6 months)
//   5. Recent Activity feed (left) — colored dots + time ago
//   6. Recent Candidates list (right) — gradient avatar + name/role·client + status pill
//
// Theme-aware: all surfaces/text/borders read from CSS tokens so light mode is
// automatically correct.
// ═════════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { requirementAPI, candidateAPI, submissionAPI, activityAPI } from '../utils/api';
import { Spinner } from '../components/UI';
import { fmtTimeRange } from '../utils/format';
import { useAuth } from '../context/AuthContext';

// ── Relative time helper ──────────────────────────────────────────────────────
function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)           return `${diff}s ago`;
  if (diff < 3600)         return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)        return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30)   return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// ── Icons (inline SVG to match the screenshot glyphs) ─────────────────────────
const I = {
  building: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="3" width="16" height="18" rx="1.5" />
      <path d="M9 8h2M13 8h2M9 12h2M13 12h2M9 16h2M13 16h2" />
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" /><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    </svg>
  ),
  doc: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M9 13h6M9 17h4" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 13l4 4L19 7" />
    </svg>
  ),
  filter: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 5h18M6 12h12M10 19h4" />
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  trendUp: (
    <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 17l6-6 4 4 4-8" /><path d="M14 7h7v7" />
    </svg>
  ),
  trendDown: (
    <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 7l6 6 4-4 4 8" /><path d="M14 17h7v-7" />
    </svg>
  ),
};

// ── Stat card ─────────────────────────────────────────────────────────────────
// ── Count-up hook ─────────────────────────────────────────────────────────────
function useCountUp(target, duration = 1200) {
  const [display, setDisplay] = useState(0);
  const raf = useRef(null);
  const prev = useRef(0);

  useEffect(() => {
    if (target === 0 || target == null) { setDisplay(0); return; }
    const start = performance.now();
    const from = prev.current;

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(from + (target - from) * eased);
      setDisplay(current);
      if (progress < 1) raf.current = requestAnimationFrame(tick);
      else prev.current = target;
    };

    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);

  return display;
}

function StatCard({ icon, tint, value, label, delta }) {
  const count = useCountUp(value ?? 0);
  const up = delta >= 0;
  const showDelta = delta !== 0;
  return (
    <div
      className="relative rounded-2xl p-5 transition-colors"
      style={{
        background: 'var(--surface-1)',
        border: '1px solid var(--border-subtle)',
        minHeight: 150,
      }}
    >
      <div className="flex items-start justify-between mb-6">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center"
          style={{ background: `${tint}1f`, color: tint, border: `1px solid ${tint}33` }}
        >
          {icon}
        </div>
        {showDelta && (
          <div
            className="inline-flex items-center gap-1 text-[11px] font-semibold tabular-nums"
            style={{ color: up ? '#22c55e' : '#ef4444' }}
          >
            {up ? I.trendUp : I.trendDown}
            <span>{Math.abs(delta)}%</span>
          </div>
        )}
      </div>
      <div
        className="text-3xl sm:text-4xl font-semibold tabular-nums leading-none mb-2 truncate"
        style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}
      >
        {count.toLocaleString()}
      </div>
      <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{label}</div>
      <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>vs last month</div>
    </div>
  );
}

// ── Pipeline bar row ──────────────────────────────────────────────────────────
function PipelineRow({ name, value, pct, gradient, animDelay = 0 }) {
  const [displayPct, setDisplayPct] = useState(0);
  const animatedCount = useCountUp(value ?? 0, 900);
  const targetPct = Math.max(pct, value > 0 ? 6 : 0);

  useEffect(() => {
    setDisplayPct(0);
    const t = setTimeout(() => {
      requestAnimationFrame(() => setDisplayPct(targetPct));
    }, animDelay + 50);
    return () => clearTimeout(t);
  }, [targetPct, animDelay]); // eslint-disable-line

  return (
    <div className="flex items-center gap-4">
      <div className="w-20 sm:w-24 text-sm flex-shrink-0 truncate min-w-0" style={{ color: 'var(--text-secondary)' }}>{name}</div>
      <div className="flex-1 relative">
        <div className="h-7 rounded-md relative overflow-hidden" style={{ background: 'var(--surface-3)' }}>
          <div
            className="h-full rounded-md flex items-center px-3"
            style={{
              width: `${displayPct}%`,
              minWidth: displayPct > 0 && value > 0 ? 32 : 0,
              background: gradient,
              boxShadow: '0 0 20px rgba(99,102,241,0.15)',
              transition: `width 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94), min-width 800ms`,
            }}
          >
            <span className="text-xs font-semibold tabular-nums text-white">{animatedCount}</span>
          </div>
        </div>
      </div>
      <div className="w-12 text-xs font-semibold text-right tabular-nums flex-shrink-0"
        style={{ color: 'var(--text-secondary)' }}>
        {pct}%
      </div>
    </div>
  );
}

// ── Grouped bar chart: Monthly Activity ───────────────────────────────────────
function MonthlyActivityChart({ data }) {
  // data: [{month, candidates, hired}]
  const max = Math.max(...data.flatMap((d) => [d.candidates, d.hired]), 1);
  const H = 180; // chart height in px

  return (
    <div style={{ position: 'relative' }}>
      {/* Legend */}
      <div className="absolute top-5 right-5 flex items-center flex-wrap gap-2 sm:gap-4 text-xs" style={{ color: 'var(--text-secondary)' }}>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#8b5cf6' }} />
          Candidates
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#10b981' }} />
          Hired
        </span>
      </div>

      <div className="mt-2 flex items-end justify-between gap-2 sm:gap-4 px-2 overflow-x-auto" style={{ height: H }}>
        {data.map((d) => (
          <div key={d.month} className="flex-1 flex flex-col items-center gap-1.5 h-full justify-end" style={{ minWidth: 40 }}>
            <div className="flex items-end gap-1 sm:gap-1.5 h-full w-full justify-center">
              <div
                className="rounded-t-md transition-all"
                style={{
                  width: '14px',
                  maxWidth: '18px',
                  flex: '0 0 auto',
                  height: `${(d.candidates / max) * (H - 20)}px`,
                  background: 'linear-gradient(180deg, #a78bfa 0%, #6366f1 100%)',
                  boxShadow: '0 0 10px rgba(139,92,246,0.3)',
                }}
                title={`${d.month}: ${d.candidates} candidates`}
              />
              <div
                className="rounded-t-md transition-all"
                style={{
                  width: '14px',
                  maxWidth: '18px',
                  flex: '0 0 auto',
                  height: `${(d.hired / max) * (H - 20)}px`,
                  background: 'linear-gradient(180deg, #34d399 0%, #059669 100%)',
                  boxShadow: '0 0 10px rgba(16,185,129,0.25)',
                }}
                title={`${d.month}: ${d.hired} hired`}
              />
            </div>
            <div className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>{d.month}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Section card shell ────────────────────────────────────────────────────────
function Panel({ title, right, children, className = '' }) {
  return (
    <div
      className={`relative rounded-2xl ${className}`}
      style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)' }}
    >
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h3>
        {right}
      </div>
      <div className="px-5 pb-5">{children}</div>
    </div>
  );
}

// ── Recent activity row (colored dot + text + time + who) ────────────────────
function ActivityItem({ color, title, time, by }) {
  return (
    <div
      className="flex items-start gap-3 py-3"
      style={{ borderBottom: '1px solid var(--border-subtle)' }}
    >
      <span
        className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
        style={{ background: color, boxShadow: `0 0 7px ${color}99` }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium leading-snug" style={{ color: 'var(--text-primary)' }}>{title}</div>
        <div className="text-xs mt-0.5 flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
          <span>{time}</span>
          {by && (
            <>
              <span>·</span>
              <span style={{ color: 'var(--text-secondary)' }}>{by}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Activity Feed "View all" modal ────────────────────────────────────────────
function ActivityModal({ onClose }) {
  const [feed,    setFeed]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    activityAPI.getDashboardFeed({ limit: 50 })
      .then(r => setFeed(r.data.feed || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Escape key
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative rounded-2xl w-full max-w-lg flex flex-col shadow-2xl"
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          maxHeight: '88vh',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{ borderBottom: '1px solid var(--border-subtle)' }}
        >
          <div>
            <div className="font-bold text-base" style={{ color: 'var(--text-primary)' }}>Recent Activity</div>
            <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Latest events across all modules</div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xl leading-none"
            style={{ color: 'var(--text-muted)', background: 'transparent', border: 'none' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
          >
            ×
          </button>
        </div>

        {/* Scrollable list — custom-scroll removes grey scrollbar track */}
        <div className="flex-1 overflow-y-auto custom-scroll px-6 py-2">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Spinner size="md" />
            </div>
          ) : feed.length === 0 ? (
            <div className="py-20 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
              No activity yet
            </div>
          ) : feed.map((a, i) => (
            <div
              key={a.id || i}
              className="flex items-start gap-3 py-3"
              style={{ borderBottom: i < feed.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}
            >
              <span
                className="block w-2 h-2 rounded-full flex-shrink-0 mt-1.5"
                style={{ background: a.color, boxShadow: `0 0 6px ${a.color}80` }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-snug" style={{ color: 'var(--text-primary)' }}>
                  {a.title}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                  <span>{timeAgo(a.time)}</span>
                  {a.by && <><span>·</span><span style={{ color: 'var(--text-secondary)' }}>{a.by}</span></>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        {!loading && feed.length > 0 && (
          <div
            className="px-6 py-3 flex-shrink-0 text-xs text-center"
            style={{ borderTop: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}
          >
            Showing {feed.length} most recent events
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ── Candidate row (gradient avatar + details + status pill) ───────────────────
function CandidateRow({ candidate }) {
  const initials = (candidate.fullName || '')
    .split(' ').filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('');
  const gradient = avatarGradient(candidate.fullName || candidate.id);
  const statusStyle = STATUS_STYLES[candidate.status] || STATUS_STYLES.Applied;

  return (
    <Link
      to={`/candidate-profile/${candidate.id}`}
      className="flex items-center gap-3 py-3 transition-colors rounded-lg -mx-2 px-2"
      style={{ textDecoration: 'none' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover-bg)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
        style={{ background: gradient, color: '#fff' }}
      >
        {initials || '?'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
          {candidate.fullName}
        </div>
        <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
          {candidate.designation || candidate.role || 'Candidate'}
          {candidate.client?.clientName ? ` · ${candidate.client.clientName}` : ''}
        </div>
      </div>
      <span
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full"
        style={{ background: statusStyle.bg, color: statusStyle.fg, border: `1px solid ${statusStyle.border}` }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusStyle.fg }} />
        {candidate.status || 'Applied'}
      </span>
    </Link>
  );
}

const STATUS_STYLES = {
  Applied:     { bg: 'rgba(59,130,246,0.12)',  fg: '#60a5fa', border: 'rgba(59,130,246,0.3)' },
  Shortlisted: { bg: 'rgba(245,158,11,0.12)',  fg: '#fbbf24', border: 'rgba(245,158,11,0.3)' },
  Interview:   { bg: 'rgba(139,92,246,0.14)',  fg: '#a78bfa', border: 'rgba(139,92,246,0.3)' },
  Hired:       { bg: 'rgba(16,185,129,0.12)',  fg: '#34d399', border: 'rgba(16,185,129,0.3)' },
  Rejected:    { bg: 'rgba(239,68,68,0.12)',   fg: '#f87171', border: 'rgba(239,68,68,0.3)' },
};

function avatarGradient(seed = '') {
  const palettes = [
    ['#6366f1', '#8b5cf6'],
    ['#10b981', '#059669'],
    ['#f59e0b', '#d97706'],
    ['#ec4899', '#db2777'],
    ['#06b6d4', '#0891b2'],
    ['#8b5cf6', '#ec4899'],
  ];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const [a, b] = palettes[h % palettes.length];
  return `linear-gradient(135deg, ${a} 0%, ${b} 100%)`;
}

// ═════════════════════════════════════════════════════════════════════════════
// Recruiter Dashboard
// ═════════════════════════════════════════════════════════════════════════════
const SUB_STATUS_STYLES = {
  Applied:     { bg: 'rgba(59,130,246,0.12)',  color: '#3b82f6',  border: 'rgba(59,130,246,0.3)'  },
  Shortlisted: { bg: 'rgba(245,158,11,0.12)',  color: '#f59e0b',  border: 'rgba(245,158,11,0.3)'  },
  Interview:   { bg: 'rgba(139,92,246,0.12)',  color: '#8b5cf6',  border: 'rgba(139,92,246,0.3)'  },
  Hired:       { bg: 'rgba(16,185,129,0.12)',  color: '#10b981',  border: 'rgba(16,185,129,0.3)'  },
  Rejected:    { bg: 'rgba(239,68,68,0.12)',   color: '#ef4444',  border: 'rgba(239,68,68,0.3)'   },
};

function RecruiterDashboard({ user }) {
  const [stats,        setStats]        = useState(null);
  const [submissions,  setSubmissions]  = useState([]);
  const [candidates,   setCandidates]   = useState([]);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, subsRes, candsRes] = await Promise.all([
          requirementAPI.getDashboardStats(),
          submissionAPI.getAll({ limit: 8, page: 1 }),
          candidateAPI.getAll({ limit: 6, page: 1, sortBy: 'updatedAt', sortOrder: 'DESC' }),
        ]);
        setStats(statsRes.data.stats);
        setSubmissions(subsRes.data.submissions || []);
        setCandidates(candsRes.data.candidates || []);
      } catch (err) {
        console.error('Recruiter dashboard load failed', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line

  if (loading) return <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>;

  const by = stats?.candidatesByStatus || {};
  const interviews = submissions.filter(s => s.status === 'Interview');
  const activeSubmissions = submissions.filter(s => s.status !== 'Rejected');

  const recruiterStats = [
    {
      icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
      tint: '#8b5cf6', value: stats?.totalCandidates ?? 0, label: 'Total Candidates',
    },
    {
      icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h4"/></svg>,
      tint: '#3b82f6', value: stats?.openRequirements ?? 0, label: 'Open Requirements',
    },
    {
      icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
      tint: '#f59e0b', value: by['Interview'] ?? 0, label: 'In Interview',
    },
    {
      icon: <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7"/></svg>,
      tint: '#10b981', value: stats?.hiredCandidates ?? 0, label: 'Hired This Month',
    },
  ];

  return (
    <div className="max-w-[1400px] mx-auto">
      {/* Header */}
      <header className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-semibold" style={{ color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
          Good {greeting()}, {user?.name?.split(' ')[0] || 'there'} 👋
        </h1>
        <p className="text-sm mt-1.5" style={{ color: 'var(--text-muted)' }}>
          Your recruiter workspace · {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </header>

      {/* Stat cards */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {recruiterStats.map(s => (
          <div key={s.label} className="rounded-2xl p-5" style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)' }}>
            <div className="flex items-start justify-between mb-4">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ background: `${s.tint}1f`, color: s.tint, border: `1px solid ${s.tint}33` }}>
                {s.icon}
              </div>
            </div>
            <div className="text-3xl sm:text-4xl font-semibold tabular-nums leading-none mb-2 truncate"
              style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
              {s.value.toLocaleString()}
            </div>
            <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{s.label}</div>
          </div>
        ))}
      </section>

      {/* Pipeline snapshot */}
      <section className="mb-5">
        <Panel title="My Pipeline" right={
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full"
            style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' }}>
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#34d399' }} />
            LIVE
          </span>
        }>
          <div className="space-y-3.5 pt-1">
            {[
              { name: 'Applied',     gradient: 'linear-gradient(90deg,#60a5fa,#3b82f6)' },
              { name: 'Shortlisted', gradient: 'linear-gradient(90deg,#fbbf24,#d97706)' },
              { name: 'Interview',   gradient: 'linear-gradient(90deg,#a78bfa,#7c3aed)' },
              { name: 'Hired',       gradient: 'linear-gradient(90deg,#34d399,#059669)' },
            ].map((p, i) => {
              const value = by[p.name] || 0;
              const total = stats?.totalCandidates || 1;
              const pct   = Math.round((value / total) * 100);
              return <PipelineRow key={p.name} name={p.name} value={value} pct={pct} gradient={p.gradient} animDelay={i * 120} />;
            })}
          </div>
        </Panel>
      </section>

      {/* Recent Submissions + Upcoming Interviews */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Recent Submissions */}
        <Panel title="My Recent Submissions" right={
          <Link to="/submissions" className="text-xs font-medium" style={{ color: 'var(--accent-text)', textDecoration: 'none' }}>View all →</Link>
        }>
          {activeSubmissions.length === 0 ? (
            <div className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No submissions yet</div>
          ) : (
            <div className="space-y-2 pt-1">
              {activeSubmissions.slice(0, 5).map(sub => {
                const ss = SUB_STATUS_STYLES[sub.status] || SUB_STATUS_STYLES.Applied;
                return (
                  <div key={sub.id} className="flex items-center gap-3 py-2.5 rounded-xl px-3 -mx-1"
                    style={{ background: 'var(--surface-2)' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ background: avatarGradient(sub.candidate?.fullName || sub.id), color: '#fff' }}>
                      {sub.candidate?.fullName?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{sub.candidate?.fullName}</p>
                      <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                        {sub.requirement?.jobTitle || 'No requirement'} · {sub.client?.clientName}
                      </p>
                    </div>
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                      style={{ background: ss.bg, color: ss.color, border: `1px solid ${ss.border}` }}>
                      {sub.status}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>

        {/* Upcoming Interviews */}
        <Panel title="Upcoming Interviews" right={
          <Link to="/interviews" className="text-xs font-medium" style={{ color: 'var(--accent-text)', textDecoration: 'none' }}>View all →</Link>
        }>
          {interviews.length === 0 ? (
            <div className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No interviews scheduled</div>
          ) : (
            <div className="space-y-2 pt-1">
              {interviews.slice(0, 5).map(sub => {
                const rounds = sub.interviewRounds || [];
                const nextRound = rounds.find(r => r.result === 'Pending') || rounds[rounds.length - 1];
                return (
                  <div key={sub.id} className="rounded-xl px-3 py-2.5 -mx-1"
                    style={{ background: 'var(--surface-2)' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{ background: avatarGradient(sub.candidate?.fullName || sub.id), color: '#fff' }}>
                        {sub.candidate?.fullName?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{sub.candidate?.fullName}</p>
                        <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                          {sub.client?.clientName}{sub.requirement?.jobTitle ? ` · ${sub.requirement.jobTitle}` : ''}
                        </p>
                      </div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{ background: 'rgba(139,92,246,0.12)', color: '#8b5cf6', border: '1px solid rgba(139,92,246,0.3)' }}>
                        R{rounds.length}
                      </span>
                    </div>
                    {nextRound?.date && (
                      <p className="text-xs mt-1.5 ml-11" style={{ color: 'var(--text-muted)' }}>
                        📅 {new Date(nextRound.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        {nextRound.time && ` · ${fmtTimeRange(nextRound.time, nextRound.endTime)}`}
                        {nextRound.mode && ` · ${nextRound.mode}`}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </section>

      {/* Recent Candidates */}
      <Panel title="Recent Candidates" right={
        <Link to="/candidates" className="text-xs font-medium" style={{ color: 'var(--accent-text)', textDecoration: 'none' }}>View all →</Link>
      }>
        {candidates.length === 0 ? (
          <div className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No candidates yet</div>
        ) : (
          <div>
            {candidates.map(c => <CandidateRow key={c.id} candidate={c} />)}
          </div>
        )}
      </Panel>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Main component — routes to role-specific dashboard
// ═════════════════════════════════════════════════════════════════════════════
export default function Dashboard() {
  const { user, isRecruiter } = useAuth();
  if (isRecruiter()) return <RecruiterDashboard user={user} />;
  return <ManagerDashboard user={user} />;
}

function ManagerDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [recentCandidates, setRecentCandidates] = useState([]);
  const [monthlyActivity, setMonthlyActivity] = useState([]);
  const [activityFeedData, setActivityFeedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActivityModal, setShowActivityModal] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, candidatesRes, monthlyRes, feedRes] = await Promise.all([
          requirementAPI.getDashboardStats(),
          candidateAPI.getAll({ limit: 20, page: 1, sortBy: 'updatedAt', sortOrder: 'DESC' }),
          requirementAPI.getMonthlyActivity(),
          activityAPI.getDashboardFeed(),
        ]);
        setStats(statsRes.data.stats);
        setRecentCandidates(candidatesRes.data.candidates || []);
        setMonthlyActivity(monthlyRes.data.data || []);
        setActivityFeedData(feedRes.data.feed || []);
      } catch (err) {
        console.error('Dashboard load failed', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line

  // ── Pipeline bars (derived from candidatesByStatus) ────────────────────────
  const pipeline = useMemo(() => {
    const by = stats?.candidatesByStatus || {};
    const total = stats?.totalCandidates || Object.values(by).reduce((s, v) => s + v, 0) || 1;
    const row = (name, gradient) => {
      const value = by[name] || 0;
      return { name, value, pct: Math.round((value / total) * 100), gradient };
    };
    return [
      row('Applied',     'linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%)'),
      row('Shortlisted', 'linear-gradient(90deg, #fbbf24 0%, #d97706 100%)'),
      row('Interview',   'linear-gradient(90deg, #a78bfa 0%, #7c3aed 100%)'),
      row('Hired',       'linear-gradient(90deg, #34d399 0%, #059669 100%)'),
    ];
  }, [stats]);

  // ── Activity feed — real events from ActivityLog ─────────────────────────────
  const activityFeed = useMemo(() => {
    return activityFeedData.map(e => ({
      color: e.color,
      title: e.title,
      time:  timeAgo(e.time),
      by:    e.by,
    }));
  }, [activityFeedData]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>;
  }

  // ── Four stat cards ────────────────────────────────────────────────────────
  const d = stats?.deltas ?? {};
  const statCards = [
    { icon: I.building, tint: '#14b8a6', value: stats?.totalClients     ?? 0,  label: 'Total Clients',      delta: d.clients      ?? 0 },
    { icon: I.user,     tint: '#8b5cf6', value: stats?.totalCandidates  ?? 0,  label: 'Total Candidates',   delta: d.candidates   ?? 0 },
    { icon: I.doc,      tint: '#10b981', value: stats?.openRequirements ?? 0,  label: 'Open Requirements',  delta: d.requirements ?? 0 },
    { icon: I.check,    tint: '#f59e0b', value: stats?.hiredCandidates  ?? 0,  label: 'Hired This Month',   delta: d.hired        ?? 0 },
  ];

  return (
    <div className="max-w-[1400px] mx-auto">
      {/* ── Header row ─────────────────────────────────────────────────────── */}
      <header className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold" style={{ color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
            Good {greeting()}, {user?.name?.split(' ')[0] || 'there'}
          </h1>
          <p className="text-sm mt-1.5" style={{ color: 'var(--text-muted)' }}>
            Here's your pipeline overview · {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
      </header>

      {/* ── Stat cards ─────────────────────────────────────────────────────── */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {statCards.map((s) => <StatCard key={s.label} {...s} />)}
      </section>

      {/* ── Hiring Pipeline + Monthly Activity ─────────────────────────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <Panel
          title="Hiring Pipeline"
          right={
            <span
              className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full"
              style={{
                background: 'rgba(16,185,129,0.15)',
                color: '#34d399',
                border: '1px solid rgba(16,185,129,0.3)',
                letterSpacing: '0.08em',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#34d399' }} />
              LIVE
            </span>
          }
        >
          <div className="space-y-3.5 pt-1">
            {pipeline.map((p, i) => <PipelineRow key={p.name} {...p} animDelay={i * 120} />)}
          </div>
        </Panel>

        <Panel title="Monthly Activity">
          <MonthlyActivityChart data={monthlyActivity} />
        </Panel>
      </section>

      {/* ── Activity "View all" modal ──────────────────────────────────────── */}
      {showActivityModal && <ActivityModal onClose={() => setShowActivityModal(false)} />}

      {/* ── Recent Activity + Recent Candidates ────────────────────────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel
          title="Recent Activity"
          right={
            <button
              onClick={() => setShowActivityModal(true)}
              className="text-xs font-medium"
              style={{ color: 'var(--accent-text)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              View all →
            </button>
          }
        >
          <div className="pt-1">
            {activityFeed.length === 0 ? (
              <div className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No recent activity</div>
            ) : (
              activityFeed.slice(0, 6).map((a, i) => <ActivityItem key={a.id || i} {...a} />)
            )}
          </div>
        </Panel>

        <Panel
          title="Recent Candidates"
          right={
            <Link to="/candidates" className="text-xs font-medium" style={{ color: 'var(--accent-text)', textDecoration: 'none' }}>
              View all →
            </Link>
          }
        >
          {recentCandidates.length ? (
            <div>
              {recentCandidates.slice(0, 5).map((c) => <CandidateRow key={c.id} candidate={c} />)}
            </div>
          ) : (
            <div className="text-sm py-10 text-center" style={{ color: 'var(--text-muted)' }}>
              No candidates yet
            </div>
          )}
        </Panel>
      </section>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}
